/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ClipId } from "types/Clip";
import { SegmentId } from "types/Asset";
import { Block, BlockId } from "types/Block";
import * as uuid from "uuid";

export interface BlockIdRequest {
  generatedId: BlockId;
  clipId: ClipId;
  segmentIndex: number;
  priority: number;
}

export type BlockFinalizer<A> = () => A;
export type BlockCreator<A> = (blockId: BlockId) => A;

export interface BlockIdGenerator {
  requestBlockId: <A>(
    clipId: ClipId,
    segmentIndex: number,
    priority: number,
    creator: BlockCreator<A>
  ) => BlockFinalizer<A>;
}

// This tries to provide a stable generator for block ids that uses the
// first clip in the block as a key to mutable state. Operations that
// change clip ids (like deletions, insertions, etc) should use `aliasClip`
// to keep the block ids stable.
// TODO: where does this state belong? is this the best place for it?
export class BlockIdStabilizer {
  private blockIndex: Record<ClipId, Record<number, BlockId>> = {};

  private getBlockId = (clipId: ClipId, segmentIndex: number): BlockId => {
    if (!this.blockIndex[clipId]) {
      this.blockIndex[clipId] = {};
    }

    if (this.blockIndex[clipId][segmentIndex]) {
      return this.blockIndex[clipId][segmentIndex];
    } else {
      const newId = uuid.v4();
      this.blockIndex[clipId][segmentIndex] = newId;
      return newId;
    }
  };

  private dealiasClip = (clipId: ClipId, segmentIndex: number): void => {
    this.blockIndex[clipId] = {
      ...this.blockIndex[clipId],
    };

    delete this.blockIndex[clipId][segmentIndex];
  };

  // Mark that clip `from` has been modified into clip `to` as a result of an insertion or
  // deletion. Copy/paste operations don't need to use this function on the pasted clips.
  public aliasClip: (_: { from: ClipId; to: ClipId }) => void = ({
    from,
    to,
  }) => {
    if (!this.blockIndex[from]) {
      this.blockIndex[from] = {};
    }
    this.blockIndex[to] = this.blockIndex[from];
  };

  /* Wrap a callback that assigns all the IDs for the blocks on a page. Once all the ids have
  been assigned, this function will check for duplicates and assign them new ids according
  to their sizes (blocks with more tokens should have more stable ids).
   */
  public withGenerator = (
    callback: (
      generator: BlockIdGenerator
    ) => Array<Block | BlockFinalizer<Block>>
  ): Block[] => {
    const generator = new BlockIdStabilizer.BlockIdPass(this);
    const finalizers = callback(generator);

    generator.dedupIds();

    return finalizers.map((finalizer) => {
      if (finalizer instanceof Function) {
        return finalizer();
      } else {
        return finalizer;
      }
    });
  };

  private static BlockIdPass = class implements BlockIdGenerator {
    // inline class to give this access to private methods on `BlockIdStabilizer`
    private tempIdMap: Record<BlockId, BlockIdRequest> = {};
    private generator: BlockIdStabilizer;

    constructor(generator: BlockIdStabilizer) {
      this.generator = generator;
    }

    public requestBlockId = <A>(
      clipId: ClipId,
      segmentIndex: number,
      priority: number,
      finalizer: (blockId: BlockId) => A
    ): BlockFinalizer<A> => {
      const tempId = uuid.v4();

      this.tempIdMap[tempId] = {
        generatedId: this.generator.getBlockId(clipId, segmentIndex),
        clipId,
        segmentIndex,
        priority,
      };

      return (): A => {
        return finalizer(this.getFinalizedId(tempId));
      };
    };

    public dedupIds = (): void => {
      const generatedIdMap: Record<string, string[]> = {};

      Object.entries(this.tempIdMap).forEach(([tempId, { generatedId }]) => {
        if (generatedIdMap[generatedId] === undefined) {
          generatedIdMap[generatedId] = [];
        }

        generatedIdMap[generatedId].push(tempId);
      });

      Object.entries(generatedIdMap).forEach(([generatedId, tempIdList]) => {
        tempIdList.sort((a, b) => {
          return this.tempIdMap[b].priority - this.tempIdMap[a].priority;
        });
        tempIdList.slice(1).forEach((tempId) => {
          const request = this.tempIdMap[tempId];
          this.generator.dealiasClip(request.clipId, request.segmentIndex);
          this.tempIdMap[tempId] = {
            ...request,
            generatedId: this.generator.getBlockId(
              request.clipId,
              request.segmentIndex
            ),
          };
        });
      });
    };

    private getFinalizedId = (tempBlockId: string): BlockId => {
      const request = this.tempIdMap[tempBlockId];
      if (request === undefined) {
        return tempBlockId;
      } else {
        return request.generatedId;
      }
    };
  };
}

export namespace BlockIdStabilizer {
  export const instance = new BlockIdStabilizer();
}
